'use client';

import { proxyFetch } from '@ps/functions/utils/proxyFetch';

/**
 * Hook that claims a lead contact for the currently-authenticated Firebase
 * user (real or just-created guest).
 *
 * Used after an anonymous user submits a lead form (which returns the lead
 * contact id) and is then promoted to a Firebase guest account — calling
 * claim links the lead to the new guest so downstream actions (e.g. slot
 * booking confirmation mail) carry the same user id.
 *
 * Backend conditions:
 *   1. The lead contact has not been claimed yet.
 *   2. Within the ~10-min window after the lead contact was created.
 *
 * Reusable across the app — not limited to the access guard.
 */
export function useClaimLeadContact() {
  async function claimLeadContact(
    storeId: string,
    leadContactId: string,
  ): Promise<void> {
    try {
      await proxyFetch('/api/bio/lead-forms/claim', {
        method: 'POST',
        body: { storeId, leadContactId },
      });
    } catch (err) {
      // Non-fatal — the caller's downstream flow (e.g. booking) should
      // continue. Backend will skip the claim if already claimed or the
      // 10-min window closed.
      console.warn('[useClaimLeadContact] claim failed:', err);
    }
  }

  return claimLeadContact;
}
